import type { CountryCode } from "@/config/country-config";
import { ProcessType } from "@/types";
import { createConsentGate } from "./consent-gate";

export type Ga4EventName =
	| "signing_started"
	| "signing_completed"
	| "signing_step_viewed"
	| "signing_login_started"
	| "signing_login_success"
	| "signing_login_failed"
	| "signing_session_timeout"
	| "signing_document_opened"
	| "signing_terms_accepted"
	| "payout_details_submitted"
	| "insurance_offer_viewed"
	| "insurance_offer_clicked"
	| "insurance_added"
	| "signing_error";

export type Ga4Market = "se" | "fi" | "no";
export type Ga4ProductType = "loan" | "credit_card" | "sales_finance";

/** `market` is added to every event from the country `Ga4Init` reports. */
export type Ga4EventParameters = {
	product_type?: Ga4ProductType;
	step_name?: string;
	step_number?: number;
	terms_type?: "insurance_offer";
	error_category?: "authentication" | "timeout" | "unknown";
};

export const GA4_MARKET: Record<CountryCode, Ga4Market> = {
	SE: "se",
	FI: "fi",
	NO: "no",
};

export const GA4_PRODUCT_TYPE: Record<ProcessType, Ga4ProductType> = {
	[ProcessType.CardSE]: "credit_card",
	[ProcessType.LoanSE]: "loan",
	[ProcessType.LoanFI]: "loan",
	[ProcessType.LoanNO]: "loan",
	[ProcessType.SalesFinanceSE]: "sales_finance",
};

export const getGa4ProductType = (
	processType: ProcessType | undefined
): Ga4ProductType | undefined =>
	processType ? GA4_PRODUCT_TYPE[processType] : undefined;

type Gtag = (...args: unknown[]) => void;

declare global {
	interface Window {
		dataLayer?: unknown[];
		gtag?: Gtag;
		[disableKey: `ga-disable-${string}`]: boolean | undefined;
	}
}

const MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]+$/;
const SCRIPT_SELECTOR = "script[data-leabank-ga4]";
const LOGIN_STARTED_SESSION_KEY = "leabank_ga4_login_started";
const SIGNING_COMPLETED_SESSION_KEY = "leabank_ga4_signing_completed";

const consentGate = createConsentGate();
let market: Ga4Market | undefined;
let configuredMeasurementId: string | undefined;

export const isGa4MeasurementId = (value: string): boolean =>
	MEASUREMENT_ID_PATTERN.test(value);

/** gtag.js runs only `Arguments` objects from the dataLayer and skips arrays. */
export const createGtag = (dataLayer: unknown[]): Gtag =>
	function gtag() {
		// eslint-disable-next-line prefer-rest-params -- a rest array is skipped
		dataLayer.push(arguments);
	};

const loadGa4 = (measurementId: string) => {
	if (
		typeof window === "undefined" ||
		configuredMeasurementId === measurementId
	) {
		return;
	}

	window.dataLayer ??= [];
	window.gtag = createGtag(window.dataLayer);
	window.gtag("consent", "default", {
		analytics_storage: "denied",
		ad_storage: "denied",
		ad_user_data: "denied",
		ad_personalization: "denied",
	});
	window.gtag("js", new Date());
	// Set on the config, not per event, so gtag.js's own hits carry no flowId either.
	window.gtag("config", measurementId, {
		send_page_view: false,
		allow_google_signals: false,
		allow_ad_personalization_signals: false,
		page_location: `${window.location.origin}/signing`,
		page_referrer: `${window.location.origin}/`,
	});

	if (!document.querySelector(SCRIPT_SELECTOR)) {
		const script = document.createElement("script");
		script.async = true;
		script.dataset.leabankGa4 = "true";
		script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
		document.head.append(script);
	}

	configuredMeasurementId = measurementId;
};

/** `ga-disable-*` stops every hit, including the cookieless pings Consent Mode still sends. */
const applyConsentMode = (measurementId: string, granted: boolean) => {
	window[`ga-disable-${measurementId}`] = !granted;
	window.gtag?.("consent", "update", {
		analytics_storage: granted ? "granted" : "denied",
	});
};

/** Storage throws when the browser blocks site data; analytics must not break a step. */
const withSessionStorage = <T>(
	action: (storage: Storage) => T,
	fallback: T
): T => {
	try {
		return action(window.sessionStorage);
	} catch {
		return fallback;
	}
};

export const setGa4AnalyticsConsent = ({
	measurementId,
	granted,
	country,
}: {
	measurementId: string | null;
	granted: boolean;
	country: CountryCode;
}): void => {
	if (typeof window === "undefined") return;
	const allowed =
		granted && measurementId !== null && isGa4MeasurementId(measurementId);
	market = GA4_MARKET[country];

	if (allowed) {
		loadGa4(measurementId);
		applyConsentMode(measurementId, true);
	} else {
		withSessionStorage(
			(storage) => storage.removeItem(LOGIN_STARTED_SESSION_KEY),
			undefined
		);
		if (configuredMeasurementId) {
			applyConsentMode(configuredMeasurementId, false);
		}
	}
	consentGate.settle(allowed);
};

const sendEvent = (name: Ga4EventName, parameters: Ga4EventParameters) =>
	window.gtag?.("event", name, { market, ...parameters });

export const markGa4LoginStarted = (): void =>
	consentGate.run(() =>
		withSessionStorage(
			(storage) => storage.setItem(LOGIN_STARTED_SESSION_KEY, "true"),
			undefined
		)
	);

export const clearGa4LoginStarted = (): void =>
	consentGate.run(() =>
		withSessionStorage(
			(storage) => storage.removeItem(LOGIN_STARTED_SESSION_KEY),
			undefined
		)
	);

export const clearGa4SigningCompleted = (): void =>
	withSessionStorage(
		(storage) => storage.removeItem(SIGNING_COMPLETED_SESSION_KEY),
		undefined
	);

export const trackGa4SigningCompletedIfPending = (
	parameters: Ga4EventParameters
): void =>
	consentGate.run(() => {
		const alreadyTracked = withSessionStorage((storage) => {
			if (storage.getItem(SIGNING_COMPLETED_SESSION_KEY) === "true") {
				return true;
			}
			storage.setItem(SIGNING_COMPLETED_SESSION_KEY, "true");
			return false;
		}, false);
		if (!alreadyTracked) sendEvent("signing_completed", parameters);
	});

export const trackGa4LoginSuccessIfPending = (
	parameters: Ga4EventParameters
): void =>
	consentGate.run(() => {
		const wasStarted = withSessionStorage((storage) => {
			const started = storage.getItem(LOGIN_STARTED_SESSION_KEY) === "true";
			storage.removeItem(LOGIN_STARTED_SESSION_KEY);
			return started;
		}, false);
		if (wasStarted) sendEvent("signing_login_success", parameters);
	});

export const trackGa4Event = (
	name: Ga4EventName,
	parameters: Ga4EventParameters = {}
): void => consentGate.run(() => sendEvent(name, parameters));
